import { Checkbox, Typography } from "@suraasa/placebo-ui"
import { Answer as AnswerType } from "api/resources/assessments/types"
import clsx from "clsx"

import styles from "../MultipleCorrect/MultipleCorrect.module.css"

export const safeHTML = (html: string) => {
  return html.replace(/[\x26<>'"]/g, function (r) {
    return "&#" + r.charCodeAt(0) + ";"
  })
}

type Props = {
  question: AnswerType
}

export const displayBlank = (
  content: string,
  color: "success" | "critical" | "surface"
) => {
  let className = ""

  switch (color) {
    case "success":
      className = "bg-success-50 border border-success-500"
      break
    case "critical":
      className = "bg-critical-50 border border-critical-500"
      break
    case "surface":
      className = "bg-surface-50 border border-surface-300"
      break
  }

  return `<h4 class="inline-block rounded-[4px] border ${className} px-1">${content}</h4>`
}

function FillBlank({ question }: Props) {
  const options = Object.entries(question.options)

  const marked = question.response ?? null

  /**
   *
   * @param content string to display for blank
   * @param index optional parameter only if answers needs to be verified with correctAnswers
   * @returns blank style as a container displaying content
   */
  const getBlankDisplay = (content: string, index?: number) => {
    if (!marked || index === undefined) return displayBlank(content, "surface")

    if (question.isCorrect) return displayBlank(content, "success")

    const correctAnswer = question.correctAnswer[index]
    if (marked[index] === null) return displayBlank(content, "surface")
    if (marked[index] === correctAnswer) return displayBlank(content, "success")
    if (marked[index] !== correctAnswer)
      return displayBlank(content, "critical")
  }

  const convert = (text: string) => {
    const questionParts: (string | undefined)[] = []
    const questionTextArray = text.split("__")

    let answerIndex = 0

    for (let i = 0; i < questionTextArray.length; i++) {
      if (questionTextArray[i] === "blank") {
        if (!marked) {
          questionParts.push(
            getBlankDisplay(new Array(18).fill("&nbsp;").join(""))
          )
        } else {
          /**
           * If user has marked an answer, then we escape the option text and render that,
           * if user has not marked an answer, we just render whitespace to get the underline blank effect.
           */
          const optionKey = marked[answerIndex]

          const markedAnswer =
            optionKey != null
              ? safeHTML(options[optionKey][1] as string)
              : new Array(11).fill("&nbsp;").join("")

          const blank = getBlankDisplay(markedAnswer, answerIndex)
          questionParts.push(blank)
        }
        answerIndex++
      } else {
        questionParts.push(questionTextArray[i])
      }
    }

    return (
      <span dangerouslySetInnerHTML={{ __html: questionParts.join(" ") }} />
    )
  }

  return (
    <>
      <div>
        <Typography
          variant="body"
          className="mb-2 mt-1"
          style={{ lineHeight: "1.65rem" }}
        >
          {convert(question.question)}
        </Typography>
      </div>

      {Object.entries(question.options).map(([optionId, optionText]) => {
        const id = parseInt(optionId)
        const markedOptionIndex =
          question.response && question.response.indexOf(id)
        const correctOptionIndex = question.correctAnswer.indexOf(id)

        const isCorrect = question.ordered
          ? markedOptionIndex === correctOptionIndex && markedOptionIndex !== -1
          : markedOptionIndex !== null &&
            markedOptionIndex !== -1 &&
            correctOptionIndex !== -1

        const isMarked = question.response && markedOptionIndex !== -1

        return (
          <div className={styles.checkbox} key={optionId}>
            <Checkbox
              containerClass={clsx(
                "my-1 flex w-full flex-row items-center gap-1 rounded-lg p-0.75 text-smallBody",
                {
                  [styles.correctAnswer]: isCorrect,
                  [styles.markedAnswer]: !isCorrect && isMarked,
                }
              )}
              color={isCorrect ? "success" : "critical"}
              checked={isCorrect || Boolean(isMarked)}
              readOnly
              label={optionText}
            />
          </div>
        )
      })}
    </>
  )
}

export default FillBlank
